import { DialogActions, DialogTitle } from "@material-ui/core";
import { marked } from "marked";
import React, { Component } from "react";
import styled from "styled-components";

import { SlideCategories } from "../../../common/SlideCategories/CategoryController";
import { Button } from "../../Components/Button";
import { Dropdown } from "../../Components/Dropdown";
import { Icon } from "../../Components/Icon";
import { IconButton } from "../../Components/IconButton";
import { MenuItem } from "../../Components/Menu";
import { ToggleSwitch } from "../../Components/ToggleSwitch";
import { WithLabel } from "../../Components/WithLabel";
import AiGenerationService, { FileIsTooLargeError } from "../../core/services/aiGeneration";
import { FileType } from "../../core/utilities/fileSourcesAndTypes";
import { BeautifulDialog, DialogContent, ShowDialog, ShowErrorDialog } from "../../react/components/Dialogs/BaseDialog";
import { Gap10, Gap20 } from "../../react/components/Gap";
import { FlexBox } from "../../react/components/LayoutGrid";
import Spinner from "../../react/components/Spinner";
import ImportFileDialog from "../../react/views/ImportFile/ImportFileDialog";
import { AiSlideContentEditor } from "../AiGeneration/GeneratePresentation/Components/AiSlideContentEditor";
import FetchingClickShield from "../../react/components/FetchingClickShield";

const EditSlideContainer = styled.div`
    width: 100%;

    textarea {
        resize: none;
        width: calc(100% - 20px);
        min-height: 300px;
        padding: 10px;
        font-family: Source Sans Pro;
        font-size: 16px;
        outline: none;
        line-height: 1.5;
        border: solid 1px #ccc;
    }
`;

const TextContainer = styled.div`
    background: #fff;
    border: solid 1px #ccc;
    padding: 20px;
    width: 100%;
    min-height: 200px;
`;

const FilesContainer = styled.div`
    display: flex;
    gap: 10px;
    padding: 5px 0px;
`;

const FileEntryContainer = styled.div`
    position: relative;
    display: flex;
    gap: 5px;
    color: #f1f1f1;
    font-size: 14px;
    border: solid 1px #C4C4C4;
    padding: 5px 10px;
    align-items: center;
    color: #555;

    &.processing {
        opacity: 0.7;
    }
`;

const FileEntryButton = styled(IconButton)`
    height: 16px;
    width: 16px;
    min-width: 16px;
    border-radius: 50%;

    &:hover {
        background: rgba(0, 0, 0, 0.1);
    }
`;

class FileEntry extends Component {
    render() {
        let { file, onRemove } = this.props;

        return (
            <FileEntryContainer className={file.isProcessing ? "processing" : ""}>
                {file.isProcessing &&
                    <div style={{ position: "relative", width: 30, height: 20, transform: "scale(0.8)", marginRight: -10 }}>
                        <Spinner small />
                    </div>
                }
                {file.hasBeenUsed && (
                    <Icon>check_circle</Icon>
                )}
                {!file.isProcessing && !file.hasBeenUsed && (
                    <Icon>upload_file</Icon>
                )}
                {file.name}
                {!file.isProcessing && !file.hasBeenUsed && (
                    <FileEntryButton icon="close" size={16} onClick={onRemove} />
                )}
            </FileEntryContainer>
        );
    }
}

export class SlideAiDialog extends Component {
    constructor(props) {
        super(props);

        let metadata = props.slide.getMetadata() ?? {
            title: "Loading..."
        };

        let prompt = metadata.slidePrompt ?? metadata.slideContent ?? "";

        if (typeof prompt === "object") {
            prompt = JSON.stringify(prompt, null, 2);
        }
        prompt = marked.parse(prompt);

        this.containerRef = React.createRef();

        this.state = {
            title: metadata.title,
            originalSlidePrompt: metadata.originalSlidePrompt,
            slidePrompt: prompt,
            slideType: "auto",
            preserveExactText: !!metadata.preserveExactText,
            files: metadata.files ? metadata.files.map(f => f.hasBeenUsedFor === "slide" ? ({ ...f, hasBeenUsed: false }) : f) : [],
            isDragging: false,
            isSavingFiles: false
        };
    }

    handleDrop = async e => {
        e.preventDefault();
        e.stopPropagation();

        this.setState({ isDragging: false });

        let droppedFiles = Array.from(e.dataTransfer.files);

        for (let file of droppedFiles) {
            await this.processFile(file);
        }
    }

    handleDragOver = e => {
        e.preventDefault();
        e.stopPropagation();

        if (!this.state.isDragging) {
            this.setState({ isDragging: true });
        }
    }

    handleDragLeave = e => {
        e.preventDefault();
        e.stopPropagation();

        if (e.currentTarget === this.containerRef.current) {
            this.setState({ isDragging: false });
        }
    }

    handleAddFile = () => {
        ShowDialog(ImportFileDialog, {
            fileTypes: [FileType.Document, FileType.Presentation, FileType.Spreadsheet],
            closeOnSuccess: true,
            onSuccess: async file => {
                if (file) {
                    this.processFile(file);
                }
            }
        });
    }

    processFile = async file => {
        try {
            while (this.state.files.some(f => f.name === file.name)) {
                file.name = `_${file.name}`;
            }

            this.setState(state => ({ ...state, files: [...state.files, { name: file.name, type: file.type, isProcessing: true }] }));

            let parsedFile = await AiGenerationService.parseDocument(file, this.state.files);

            this.setState(state => ({ ...state, files: [...state.files.filter(f => f.name !== file.name), parsedFile] }));
        } catch (err) {
            this.setState(state => ({ ...state, files: state.files.filter(f => f.name !== file.name) }));

            if (err instanceof FileIsTooLargeError) {
                ShowErrorDialog({
                    title: "File is too large",
                    message: err.message,
                });
                return;
            }

            ShowErrorDialog({
                title: `Error processing ${file.name}`,
                message: "Sorry, we couldn't process the file. Please try again with a different file.",
            });
        }
    }

    handleRemoveFile = file => {
        this.setState(state => ({ ...state, files: state.files.filter(f => f.name !== file.name) }));
    }

    handleRegenerateSlide = async () => {
        let { closeDialog } = this.props;
        let { files, slidePrompt, slideType, preserveExactText, title, originalSlidePrompt } = this.state;

        let slideFiles = files.filter(f => !f.hasBeenUsed);
        if (slideFiles.length > 0) {
            this.setState({ isSavingFiles: true });
            let { files: savedFiles } = await AiGenerationService.saveContextDocuments(slideFiles);
            slideFiles = savedFiles;
            slideFiles.forEach(f => {
                f.hasBeenUsed = true;
                f.hasBeenUsedFor = "slide";
            });
        }
        files = files.map(f => {
            let slideFile = slideFiles.find(sf => sf.name === f.name);
            return slideFile ?? f;
        });

        closeDialog({
            title,
            originalSlidePrompt,
            files,
            slidePrompt,
            slideType: slideType === "auto" ? null : slideType,
            preserveExactText
        });
    }

    render() {
        let { closeDialog } = this.props;
        let { slidePrompt, slideType, preserveExactText, files, isDragging, isSavingFiles } = this.state;

        let hasProcessingFiles = files.some(f => f.isProcessing);
        let slideFiles = files.filter(f => !f.hasBeenUsed);

        return (
            <BeautifulDialog closeDialog={closeDialog} maxWidth="md">
                <DialogTitle>Regenerate Slide</DialogTitle>
                <DialogContent
                    containerRef={this.containerRef}
                    onDrop={this.handleDrop}
                    onDragOver={this.handleDragOver}
                    onDragLeave={this.handleDragLeave}
                >
                    <FetchingClickShield visible={isSavingFiles} backgroundColor="white" />
                    <EditSlideContainer>
                        <WithLabel label="Slide Prompt" above left>
                            <TextContainer>
                                <AiSlideContentEditor text={slidePrompt} onChange={slidePrompt => this.setState({ slidePrompt })} />
                            </TextContainer>
                        </WithLabel>
                        <Gap10 />
                        {slideFiles.length > 0 && <>
                            <FilesContainer>
                                {slideFiles.map((file, index) => (
                                    <FileEntry key={index} file={file} onRemove={() => this.handleRemoveFile(file)} />
                                ))}
                            </FilesContainer>
                            <Gap10 />
                        </>}
                        <FlexBox left middle fillWidth gap={20}>
                            <WithLabel label="Slide Type" tight>
                                <Dropdown value={slideType}
                                    onChange={slideType => this.setState({ slideType })}>
                                    <MenuItem key="auto" value="auto">Auto Detect</MenuItem>
                                    {Object.values(SlideCategories).map(type => <MenuItem key={type} value={type}>{type.toTitleCase()}</MenuItem>)}
                                </Dropdown>
                            </WithLabel>
                            <IconButton
                                icon={isDragging ? "check_circle" : "add_circle"}
                                label={isDragging ? "Drop files here" : "Add file..."}
                                onClick={this.handleAddFile}
                            />
                            <WithLabel tight label="Preserve Exact Text">
                                <ToggleSwitch value={preserveExactText} onChange={preserveExactText => this.setState({ preserveExactText })} />
                            </WithLabel>
                        </FlexBox>
                        <Gap20 />
                    </EditSlideContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => closeDialog(null)}>Cancel</Button>
                    <Button onClick={this.handleRegenerateSlide} disabled={hasProcessingFiles}>Regenerate Slide</Button>
                </DialogActions>
            </BeautifulDialog>
        );
    }
}
